import { useMemo, useState } from 'react';
import { Popover } from './Popover';
import { Chevron } from './Select';

/**
 * Calendar built from scratch.
 *
 * No `<input type="date">`: its picker is browser chrome we cannot style, its
 * keyboard behaviour differs across platforms, and on some it silently reformats
 * what the dispatcher typed. Dates here are plain YYYY-MM-DD strings and never
 * pass through a Date constructor that could shift them across a timezone.
 */

const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function parts(iso: string): { year: number; month: number; day: number } {
  const [y, m, d] = iso.split('-').map(Number);
  return { year: y ?? 1970, month: (m ?? 1) - 1, day: d ?? 1 };
}

function toIso(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/** Monday-first weekday index for the first of the month. */
function leadingBlanks(year: number, month: number): number {
  const sunday0 = new Date(Date.UTC(year, month, 1)).getUTCDay();
  return (sunday0 + 6) % 7;
}

function shiftIso(iso: string, days: number): string {
  const { year, month, day } = parts(iso);
  const shifted = new Date(Date.UTC(year, month, day + days));
  return toIso(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate());
}

export function DatePicker({
  value,
  today,
  onChange,
}: {
  value: string;
  today: string;
  onChange: (iso: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => {
    const p = parts(value);
    return { year: p.year, month: p.month };
  });

  const cells = useMemo(() => {
    const blanks = leadingBlanks(view.year, view.month);
    const count = daysInMonth(view.year, view.month);
    const out: (string | null)[] = Array.from({ length: blanks }, () => null);
    for (let d = 1; d <= count; d += 1) out.push(toIso(view.year, view.month, d));
    return out;
  }, [view]);

  const label = useMemo(() => {
    const p = parts(value);
    const weekday = new Date(Date.UTC(p.year, p.month, p.day)).toLocaleDateString('en-US', {
      weekday: 'short',
      timeZone: 'UTC',
    });
    return value === today ? `Today, ${weekday} ${p.day} ${MONTHS[p.month]?.slice(0, 3)}`
      : `${weekday} ${p.day} ${MONTHS[p.month]?.slice(0, 3)} ${p.year}`;
  }, [value, today]);

  const step = (days: number) => onChange(shiftIso(value, days));

  return (
    <div className="pop-anchor" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => step(-1)} aria-label="Previous day">
        <Arrow dir="left" />
      </button>

      <button
        type="button"
        className="btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          const p = parts(value);
          setView({ year: p.year, month: p.month });
          setOpen((o) => !o);
        }}
      >
        <CalendarIcon />
        {label}
        <Chevron />
      </button>

      <button type="button" className="btn btn-ghost btn-sm" onClick={() => step(1)} aria-label="Next day">
        <Arrow dir="right" />
      </button>

      <Popover open={open} onClose={() => setOpen(false)}>
        <div className="cal">
          <div className="cal-head">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() =>
                setView((v) =>
                  v.month === 0 ? { year: v.year - 1, month: 11 } : { ...v, month: v.month - 1 },
                )
              }
              aria-label="Previous month"
            >
              <Arrow dir="left" />
            </button>
            <strong>
              {MONTHS[view.month]} {view.year}
            </strong>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() =>
                setView((v) =>
                  v.month === 11 ? { year: v.year + 1, month: 0 } : { ...v, month: v.month + 1 },
                )
              }
              aria-label="Next month"
            >
              <Arrow dir="right" />
            </button>
          </div>

          <div className="cal-grid">
            {DOW.map((d, i) => (
              <div key={`${d}-${i}`} className="cal-dow">
                {d}
              </div>
            ))}
            {cells.map((iso, i) =>
              iso === null ? (
                <div key={`blank-${i}`} />
              ) : (
                <button
                  key={iso}
                  type="button"
                  className="cal-day"
                  data-selected={iso === value}
                  data-today={iso === today}
                  onClick={() => {
                    onChange(iso);
                    setOpen(false);
                  }}
                >
                  {Number(iso.slice(8))}
                </button>
              ),
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8 }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                onChange(today);
                setOpen(false);
              }}
            >
              Jump to today
            </button>
          </div>
        </div>
      </Popover>
    </div>
  );
}

function Arrow({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
      <path
        d={dir === 'left' ? 'M6.5 2l-3 3 3 3' : 'M3.5 2l3 3-3 3'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 14 14" aria-hidden="true">
      <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M1.5 5.5h11M4.5 1.5v2M9.5 1.5v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
